import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminApi } from '../../lib/api';
import { flag } from '../../config';
import lazyAccountPage from '../../components/site/auth/lazyAccountPage';

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const AdminLoginSite = lazyAccountPage(
  'AdminLoginSite',
  () => import(/* webpackChunkName: "account-pages" */ '../account/AdminLoginSite'),
);

/** /admin/login: the redesigned page with SLOT_MODEL on, the legacy page (unchanged) off. */
export default function AdminLogin() {
  return flag('SLOT_MODEL') ? <AdminLoginSite /> : <AdminLoginLegacy />;
}

const AdminLoginLegacy = () => {
  
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await adminApi.post(`/auth/login`, credentials);
      
      // Store unified token
      localStorage.setItem('token', response.data.access_token);
      
      // Remove legacy admin token if exists
      localStorage.removeItem('adminToken');
      
      // Get user info to check role
      const userResponse = await adminApi.get(`/auth/me`);
      
      const userRole = userResponse.data.role;
      
      // Navigate based on role - use window.location for full page reload
      // This ensures AuthContext re-initializes with the new token
      if (userRole === 'admin') {
        window.location.href = '/admin/dashboard';
      } else if (userRole === 'advertiser') {
        window.location.href = '/business/dashboard';
      } else {
        // Regular consumers go to Deal Wallet page
        window.location.href = '/deal-wallet';
      }
    } catch (err) {
      console.error('🔴 Login error:', err);
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#1e1b4b' }}>
      <div className="w-full max-w-md rounded-3xl p-8 shadow-2xl" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="text-2xl font-bold text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>Tappd</span>
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold tracking-widest text-white bg-red-600">ADMIN</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1e1b4b]" style={{ fontFamily: "'Playfair Display', serif" }}>Admin Sign In</h1>
          <p className="text-sm text-[#1e1b4b]/60 mt-2">Platform administration. Advertisers and venues sign in from the homepage.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-[#1e1b4b] mb-1">Email</label>
            <Input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              required
              disabled={isLoading}
              placeholder="you@example.com"
              className="h-11 bg-white"
            />
          </div>

          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-[#1e1b4b] mb-1">Password</label>
            <div className="relative">
              <Input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                disabled={isLoading}
                placeholder="Your password"
                className="h-11 pr-11 bg-white"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[#1e1b4b]/50 hover:text-[#1e1b4b]"
                disabled={isLoading}
              >
                {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {error && (
            <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <Button type="submit" disabled={isLoading} className="w-full h-11 font-bold text-white" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#1e1b4b]/60 hover:text-[#1e1b4b] min-h-[44px]">
            <Home className="w-4 h-4" aria-hidden="true" />
            Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
};
